import React, { useRef, useState, useEffect } from 'react';
import { Girl } from '../../types';
import { trackGirlClick } from '../../utils/analytics';

interface GirlCardProps {
  girl: Girl;
  onClick: () => void;
}

const GirlCard: React.FC<GirlCardProps> = ({ girl, onClick }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);

  const handleClick = () => {
    trackGirlClick(girl.name);
    onClick();
  };

  useEffect(() => {
    // Auto-play video on all devices
    if (videoRef.current && girl.video && !videoError) {
      videoRef.current.play().catch((error) => {
        console.error('Failed to auto-play video:', error);
      });
    }
  }, [girl.video, videoError]);

  const handleVideoError = () => {
    console.error('Video failed to load:', girl.video);
    setVideoError(true);
  };

  return (
    <div
      className="relative rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity aspect-[3/4] lg:aspect-auto lg:h-[380px]"
      onClick={handleClick}
    >
      {girl.video && !videoError ? (
        <video
          ref={videoRef}
          preload="auto"
          muted
          loop
          playsInline
          webkit-playsinline="true"
          autoPlay={false}
          className="w-full h-full object-cover"
          onError={handleVideoError}
          style={{ WebkitTransform: 'translateZ(0)' }}
        >
          <source src={girl.video} type="video/mp4" />
        </video>
      ) : girl.image ? (
        <img
          src={girl.image}
          alt={girl.name}
          className="w-full h-full object-cover"
        />
      ) : null}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

      {/* Girl info */}
      <div className="absolute bottom-0 left-0 right-0 p-[5px]">
        <div className="flex items-baseline gap-1 mb-2">
          <span className=" text-white font-poppins font-semibold text-xs lg:text-base">
            {girl.name},
          </span>
          <span className="text-white font-poppins"> {girl.age}</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1">
          {girl.tags.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-0.5 text-white/90 font-poppins text-[10px] font-medium backdrop-blur-sm rounded-xl"
              style={{
                background:
                  'linear-gradient(339.14deg, rgba(20, 0, 48, 0.3) 18.69%, rgba(132, 0, 255, 0.3) 81.5%)',
                border: '0.6px solid',
                borderImageSource:
                  'linear-gradient(180deg, rgba(255, 255, 255, 0.12) 0%, rgba(121, 83, 148, 0.12) 100%)',
              }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GirlCard;
